import { GetStaticPropsContext } from "next";
import React from "react";
import { ContentPageFeatures } from "../components/contentPageFeatures/contentPageFeatures.comp";
import { ContentPageHero } from "../components/contentPageHero/contentPageHero.comp";
import { ExternalSectionRenderer } from "../components/externalSections";
import { Faq } from "../components/faq/faq.comp";
import { Layout } from "../components/layout/layout.comp";
import { TrofeoBanner } from "../components/trofeoBanner/trofeoBanner.comp";
import { JsonLd } from "../components/jsonLd/jsonLd.comp";
import { getDataFromDB } from "../lib/mongodb";
import { buildBreadcrumbs } from "../lib/schema";
import { IContentPage, IExternalContentPage } from "../types";

type PageType = "content" | "external";

// Vertical pages where the Trofeo prediction-pool banner is shown.
const TROFEO_VERTICAL_SLUGS = new Set([
  "vertical/international/world-cup-bracket",
]);

function normalizeSlug(slug: string): string {
  return (slug.startsWith("/") ? slug.substring(1) : slug).toLowerCase();
}

interface ContentPageProps {
  contentPage?: IContentPage;
  externalPage?: IExternalContentPage;
  pageType: PageType;
  slug: string;
  // Real names for the parent pages in the path, so the breadcrumb trail reads
  // "Sports & Verticals > Tennis > Wimbledon Bracket" rather than a slug.
  ancestorNames?: Record<string, string>;
}

export default function ContentPage({
  contentPage,
  externalPage,
  pageType,
  slug,
  ancestorNames = {},
}: ContentPageProps) {
  const showTrofeoBanner = TROFEO_VERTICAL_SLUGS.has(normalizeSlug(slug));
  const currentName =
    externalPage?.name || contentPage?.name ||
    externalPage?.meta?.title || contentPage?.meta?.title || "";
  const breadcrumbs = buildBreadcrumbs(normalizeSlug(slug), currentName, ancestorNames);

  // Render External Content Page
  if (pageType === "external" && externalPage) {
    return (
      <Layout
        showFooter={true}
        showHeader={true}
        isDark
        title={externalPage.meta?.title}
        description={externalPage.meta?.description}
        image={externalPage.meta?.imgUrl}
        canonicalUrl={externalPage.meta?.canonicalUrl}
        url={`https://www.bracketsninja.com${slug[0] === "/" ? slug : `/${slug}`}`}
      >
        {breadcrumbs && <JsonLd data={breadcrumbs} />}
        {showTrofeoBanner && (
          <div
            style={{ maxWidth: 1020, margin: "0 auto", padding: "30px 20px 0" }}
          >
            <TrofeoBanner slug={normalizeSlug(slug)} />
          </div>
        )}
        <ExternalSectionRenderer sections={externalPage.sections || []} />
      </Layout>
    );
  }

  // Render Content Page (legacy)
  if (contentPage) {
    return (
      <Layout
        showFooter={true}
        showHeader={true}
        isDark
        title={contentPage.meta?.title}
        description={contentPage.meta?.description}
        image={contentPage.meta?.imgUrl}
        url={`https://www.bracketsninja.com${slug[0] === "/" ? slug : `/${slug}`}`}
      >
        {breadcrumbs && <JsonLd data={breadcrumbs} />}
        {showTrofeoBanner && (
          <div
            style={{ maxWidth: 1020, margin: "0 auto", padding: "30px 20px 0" }}
          >
            <TrofeoBanner slug={normalizeSlug(slug)} />
          </div>
        )}
        <ContentPageHero
          title={contentPage.name || ""}
          subtitle={contentPage.subTitle || ""}
          text={contentPage.teaser}
          isFullPage={contentPage.isFullPage}
        />
        <ContentPageFeatures contentPage={contentPage} />
        {contentPage?.faq?.length > 0 && (
          <Faq faqs={contentPage.faq.map((faq) => faq)} />
        )}
      </Layout>
    );
  }

  return null;
}

// Helper to normalize slug for path generation
function normalizeSlugForPath(slug: string): string[] {
  const normalized = slug.startsWith("/") ? slug.substring(1) : slug;
  return normalized.split("/");
}

// Determine status filter based on environment
function getStatusFilter(): { status: string } | { status: { $in: string[] } } {
  const isDev = process.env.NODE_ENV === "development";
  return isDev
    ? { status: { $in: ["published", "draft"] } }
    : { status: "published" };
}

export async function getStaticPaths() {
  try {
    const statusFilter = getStatusFilter();

    // Fetch external content pages (new system)
    const externalPages = await getDataFromDB<IExternalContentPage>(
      "external_content_pages",
      {
        ...statusFilter,
        product: "brackets",
      },
      { slug: 1, status: 1 }
    );

    // Fetch legacy content pages
    const contentPages = await getDataFromDB<IContentPage>(
      "content_pages",
      {
        ...statusFilter,
        isBrackets: true,
      },
      { slug: 1, status: 1 }
    );

    // Use a Map to deduplicate all paths by normalized slug
    // External pages are added first, so they take priority
    const pathMap = new Map<string, { params: { slug: string[] } }>();

    // Add external pages first (they take priority)
    for (const page of externalPages) {
      const normalizedKey = (page.slug.startsWith("/")
        ? page.slug.substring(1)
        : page.slug).toLowerCase();
      
      if (!pathMap.has(normalizedKey)) {
        pathMap.set(normalizedKey, {
          params: { slug: normalizeSlugForPath(page.slug) },
        });
      }
    }

    // Add content pages (only if slug doesn't already exist)
    for (const page of contentPages) {
      const normalizedKey = (page.slug.startsWith("/")
        ? page.slug.substring(1)
        : page.slug).toLowerCase();
      
      if (!pathMap.has(normalizedKey)) {
        pathMap.set(normalizedKey, {
          params: { slug: normalizeSlugForPath(page.slug) },
        });
      }
    }

    const paths = Array.from(pathMap.values());
    return { paths, fallback: false };
  } catch (e) {
    console.error(e);
    return { paths: [], fallback: false };
  }
}

/**
 * Reads the real page names for every parent path, so the breadcrumb trail can
 * say "Sports & Verticals" and "Tennis" rather than echoing the slug. Missing
 * ancestors are simply absent and the builder falls back to a readable label.
 */
async function getAncestorNames(segments: string[]): Promise<Record<string, string>> {
  const parents = segments
    .slice(0, -1)
    .map((_, index) => segments.slice(0, index + 1).join("/"))
    .filter(Boolean);

  if (!parents.length) return {};

  try {
    const pages = await getDataFromDB<IExternalContentPage>(
      "external_content_pages",
      {
        slug: { $in: parents.flatMap((path) => [path, `/${path}`]) },
        product: "brackets",
      },
      { slug: 1, name: 1 }
    );

    return pages.reduce((acc: Record<string, string>, page) => {
      const key = (page.slug || "").replace(/^\/+/, "");
      if (key && page.name) acc[key] = page.name;
      return acc;
    }, {});
  } catch (e) {
    console.error("breadcrumb ancestor lookup failed", e);
    return {};
  }
}

export async function getStaticProps({
  params = { slug: [] },
}: GetStaticPropsContext<{ slug: string[] }>) {
  const { slug } = params;
  const slugString = slug.join("/");
  const statusFilter = getStatusFilter();
  const ancestorNames = await getAncestorNames(slug);

  // Try external content pages first (higher priority)
  const externalPages = await getDataFromDB<IExternalContentPage>(
    "external_content_pages",
    {
      slug: { $regex: `^/?${slugString}$`, $options: "i" },
      product: "brackets",
      ...statusFilter,
    }
  );

  if (externalPages.length > 0) {
    return {
      props: {
        externalPage: externalPages[0],
        pageType: "external" as PageType,
        slug: slugString,
        ancestorNames,
      },
    };
  }

  // Fall back to legacy content pages
  const contentPages = await getDataFromDB<IContentPage>("content_pages", {
    slug: { $regex: slugString, $options: "i" },
    isBrackets: true,
    ...statusFilter,
  });

  return {
    props: {
      contentPage: contentPages[0] || null,
      pageType: "content" as PageType,
      slug: slugString,
      ancestorNames,
    },
  };
}
